import React from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

export const AccountUsageChart = ({ currentUsage, limit, tier }) => {
  const percentage = limit ? Math.round((currentUsage / limit) * 100) : 0;

  const data = {
    labels: ["Used", "Available"],
    datasets: [
      {
        data: [currentUsage, Math.max(0, limit - currentUsage)],
        backgroundColor: [
          percentage >= 90
            ? "#EF4444"
            : percentage >= 75
              ? "#F59E0B"
              : "#10B981",
          "#F3F4F6",
        ],
        borderColor: [
          percentage >= 90
            ? "#DC2626"
            : percentage >= 75
              ? "#D97706"
              : "#059669",
          "#E5E7EB",
        ],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label;
            const value = context.parsed;
            return `${label}: ${value} accounts`;
          },
        },
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Account Usage
      </h3>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="h-32 relative">
            <Doughnut data={data} options={options} />
          </div>
        </div>
        <div className="ml-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">
              {currentUsage}
            </div>
            <div className="text-sm text-gray-500">of {limit}</div>
            <div className="text-xs text-gray-400 mt-1">{tier} Plan</div>
          </div>
          <div className="mt-4">
            <div
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                percentage >= 90
                  ? "bg-red-100 text-red-800"
                  : percentage >= 75
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
              }`}
            >
              {percentage}% Used
            </div>
          </div>
        </div>
      </div>
      {percentage >= 90 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            You're approaching your account limit. Consider upgrading your plan.
          </p>
        </div>
      )}
    </div>
  );
};

export const APICallsChart = ({ apiCallsData }) => {
  if (!apiCallsData || apiCallsData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          API Calls (Last 30 Days)
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No API call data available
        </div>
      </div>
    );
  }

  const data = {
    labels: apiCallsData.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "API Calls",
        data: apiCallsData.map((item) => item.calls),
        borderColor: "#3B82F6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: "Date",
        },
        grid: {
          color: "#F3F4F6",
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: "API Calls",
        },
        beginAtZero: true,
        grid: {
          color: "#F3F4F6",
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  const totalCalls = apiCallsData.reduce((sum, item) => sum + item.calls, 0);
  const avgCalls = Math.round(totalCalls / apiCallsData.length);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          API Calls (Last 30 Days)
        </h3>
        <div className="flex space-x-4 text-sm">
          <div>
            <span className="text-gray-500">Total:</span>
            <span className="ml-1 font-medium text-gray-900">
              {totalCalls.toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Avg:</span>
            <span className="ml-1 font-medium text-gray-900">
              {avgCalls.toLocaleString()}/day
            </span>
          </div>
        </div>
      </div>
      <div className="h-64">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export const UsageTrendsChart = ({ usageData, timeframe = "30d" }) => {
  if (!usageData || usageData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Usage Trends
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No usage data available
        </div>
      </div>
    );
  }

  const data = {
    labels: usageData.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "Data Fetched (MB)",
        data: usageData.map((item) => item.dataFetched),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
        borderColor: "#22C55E",
        borderWidth: 1,
      },
      {
        label: "Posts Analyzed",
        data: usageData.map((item) => item.postsAnalyzed),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "#3B82F6",
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          color: "#F3F4F6",
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          color: "#F3F4F6",
        },
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Trends</h3>
      <div className="h-64">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};
